import React, { useState, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Box
} from "@mui/material";
import {
  AuthContainer,
  AuthCard,
  AuthLogo,
  LogoIcon,
  AuthStepIndicator,
  AuthStep,
  AuthStepLine,
  AuthForm,
  IdInput,
  VerificationCodeDisplay,
  CodeDisplay,
  VerificationInputContainer,
  VerificationInput,
  AuthButton,
  AuthError,
  AuthInfo,
  LoadingSpinner
} from "./Authentication.styles"; // Import styled components

export default function Authentication({ onAuthenticationSuccess }) {
    const [loginStep, setLoginStep] = useState(1);
    const [idNumber, setIdNumber] = useState("");
    const [verificationCode, setVerificationCode] = useState("");
    const [verificationCodeInput, setVerificationCodeInput] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Generate a random verification code when ID is entered
    useEffect(() => {
        if (loginStep === 2) {
            // Generate a 6-digit verification code
            const code = Math.floor(100000 + Math.random() * 900000).toString();
            setVerificationCode(code);
        }
    }, [loginStep]);

    const handleLoginNext = () => {
        setError("");

        if (loginStep === 1) {
            // Validate ID number
            if (!idNumber || idNumber.length < 8) {
                setError("Please enter a valid ID number (at least 8 digits)");
                return;
            }
            setLoginStep(2);
        } else if (loginStep === 2) {
            // Validate verification code
            if (verificationCodeInput !== verificationCode) {
                setError("Invalid verification code. Please try again.");
                return;
            }

            setLoading(true);
            // Simulate API call
            setTimeout(() => {
                onAuthenticationSuccess(idNumber); // Pass the validated ID number back
                // No need to setLoading(false) here as the component will unmount
            }, 1000);
        }
    };

    // Handle Enter key press for forms
    const handleKeyPress = (event, step) => {
        if (event.key === 'Enter') {
            if (step === 1 && idNumber && idNumber.length >= 8) {
                handleLoginNext();
            } else if (step === 2 && verificationCodeInput.length === 6) {
                handleLoginNext();
            }
        }
    };

    return (
        <AuthContainer>
            <AuthCard>
                <AuthLogo>
                    <LogoIcon>W</LogoIcon>
                    <Typography variant="h5" component="h1" color="text.secondary">
                        Will & Testament Creator
                    </Typography>
                </AuthLogo>

                <AuthStepIndicator>
                    <AuthStep active={loginStep >= 1}>1</AuthStep>
                    <AuthStepLine />
                    <AuthStep active={loginStep >= 2}>2</AuthStep>
                </AuthStepIndicator>

                {loginStep === 1 && (
                    <AuthForm>
                        <Typography variant="h6" component="h2" align="center" color="text.secondary">
                            Secure Verification
                        </Typography>
                        <Typography variant="body2" align="center" color="text.secondary">
                            Enter your ID number to begin creating your will
                        </Typography>

                        <FormControl fullWidth margin="normal">
                            <IdInput
                                id="idNumber"
                                label="National ID Number"
                                variant="outlined"
                                value={idNumber}
                                onChange={(e) => setIdNumber(e.target.value)}
                                placeholder="Enter your ID number"
                                onKeyPress={(e) => handleKeyPress(e, 1)}
                                autoFocus
                                error={!!error && !idNumber}
                            />
                        </FormControl>

                        {error && <AuthError>{error}</AuthError>}

                        <AuthButton
                            variant="contained"
                            onClick={handleLoginNext}
                            disabled={!idNumber || idNumber.length < 8}
                        >
                            Next
                        </AuthButton>

                        <AuthInfo>
                            Your information is encrypted and secure
                        </AuthInfo>
                    </AuthForm>
                )}

                {loginStep === 2 && (
                    <AuthForm>
                        <Typography variant="h6" component="h2" align="center" color="text.secondary">
                            Verification Code
                        </Typography>
                        <Typography variant="body2" align="center" color="text.secondary">
                            We've sent a verification code to your mobile device
                        </Typography>

                        <VerificationCodeDisplay>
                            <Typography variant="body2">For demo purposes, your code is:</Typography>
                            <CodeDisplay>{verificationCode}</CodeDisplay>
                        </VerificationCodeDisplay>

                        <FormControl fullWidth margin="normal">
                            <VerificationInputContainer>
                                <VerificationInput
                                    id="verificationCode"
                                    label="Verification Code"
                                    variant="outlined"
                                    value={verificationCodeInput}
                                    onChange={(e) => setVerificationCodeInput(e.target.value)}
                                    inputProps={{ maxLength: 6 }}
                                    placeholder="000000"
                                    onKeyPress={(e) => handleKeyPress(e, 2)}
                                    autoFocus
                                    error={!!error && verificationCodeInput !== verificationCode}
                                />
                            </VerificationInputContainer>
                        </FormControl>

                        {error && <AuthError>{error}</AuthError>}

                        <AuthButton
                            variant="contained"
                            onClick={handleLoginNext}
                            disabled={loading || verificationCodeInput.length !== 6}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Verify & Continue"}
                        </AuthButton>

                        <AuthButton
                            variant="outlined"
                            secondary="true"
                            onClick={() => setLoginStep(1)}
                            disabled={loading}
                        >
                            Back
                        </AuthButton>
                    </AuthForm>
                )}
            </AuthCard>
        </AuthContainer>
    );
}